import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ErroApi } from '../api';
import {
  ACONDICIONAMENTO, data, faixaProfundidade, numero, paraCampo, paraNumero,
  rotulo, rotuloDoAcondicionamento,
} from '../formato';
import {
  ColunaOrdenavel, Dialogo, ErroDaApi, type Opcao, Selo, Vazio,
} from '../ui';
import type { Tom } from '../ui';
import { Lixeira, useLixeira } from '../Lixeira';
import { NomeDaAmostra, daApi, nomeDaAmostra } from '../NomeDaAmostra';

/**
 * O tom da situação da amostra recebida (256, §9.2): a aceita é «ok», a
 * com ressalva pede atenção, a rejeitada é crítica, e a que saiu do
 * laboratório (consumida, devolvida, descartada) fica neutra. O selo leva a
 * palavra junto — nunca só a cor.
 */
export const TOM_DA_AMOSTRA: Record<string, Tom> = {
  recebida: 'ok', aceita: 'ok', aceita_com_ressalva: 'atencao',
  rejeitada: 'critico', armazenada: 'ok', em_ensaio: 'andamento',
  subdividida: 'andamento', consumida: 'neutro', devolvida: 'neutro',
  descartada: 'neutro',
};

/**
 * O quadro de amostras, com a edição rápida.
 *
 * «Crie uma "Edição rápida" onde é possível corrigir Identificação,
 *  Furo/Ponto, Profundidade (m), acondicionamento. Além disso, inclua
 *  coordenada da amostra (UTM ou geográficas). Isso pode ser editado em
 *  Amostras ou em OS, por quem tiver a autorização» — Humberto,
 *  21/09/2026. E, na mesma conversa: «Não crie um botão de edição por
 *  linha. Crie um botão de Edição Rápida que libera a edição dos campos,
 *  depois ao clicar novamente ele salva».
 *
 * ── Por que um componente, e não a tabela copiada nas duas telas ─────
 *
 * Porque «pode ser editado em Amostras ou em OS» pede a MESMA tabela em
 * dois lugares, e duas cópias do mesmo comportamento é como as duas
 * divergem (`D-29`): uma ganha a coluna de coordenada, a outra não; uma
 * pede o motivo, a outra grava calado. Aqui a tabela é uma só, e cada
 * tela lhe diz o que tem de diferente — a coluna de fotos do
 * recebimento, os cabeçalhos que ordenam (0185), o botão de acrescentar.
 *
 * ── Por que a gravação é uma só ─────────────────────────────────────
 *
 * «Ao clicar novamente ele salva»: quem destravou a tabela corrigiu doze
 * furos e um acondicionamento, e isso é UMA correção, com UM motivo. A
 * rota `PATCH /amostras/edicao-rapida` grava tudo numa transação.
 *
 * ── Quem decide se pede motivo é o servidor ─────────────────────────
 *
 * A tela não tenta adivinhar. Ela manda; se a API recusar apontando o
 * campo `motivo`, a janela abre e a tela manda de novo com o motivo
 * escrito. É o mesmo caminho da reemissão do relatório de recebimento, e
 * evita a tela e o servidor discordando sobre o que é «primeira vez».
 *
 * ── As linhas que ainda não chegaram (onda 204) ─────────────────────
 *
 * «A ideia seria unificar essas 2 tabelas em uma só para simplificar a
 *  conferência» — Humberto, 22/09/2026, sobre a ficha da OS, que tinha a
 * tabela das previstas em cima e esta embaixo. A ficha passa a montar UMA
 * tabela: cada amostra prevista é uma linha, e a linha ganha o código
 * quando a amostra chega. A tela dona manda as que ainda não chegaram em
 * `esperadas`, com a `ordem` da previsão, e marca a mesma `ordem` nas
 * recebidas que atenderam uma previsão; o quadro intercala pelas ordens e
 * põe no fim o que chegou sem ter sido previsto. A Edição rápida continua
 * valendo só para as recebidas — uma previsão se corrige na programação.
 *
 * ── A coluna «Coleta» saiu (onda 204) ───────────────────────────────
 *
 * `data_coleta` é a data em que o material foi tirado do solo. Entra no
 * recebimento como campo do LOTE (uma data para a remessa inteira), e
 * por isso aqui ou vinha em branco ou repetia o mesmo dia em toda linha.
 * O campo fica — na ficha da amostra, no relatório de recebimento e no
 * mapa, que é onde pesa; a coluna sai da tabela.
 */

export interface AmostraDoQuadro {
  id: string;
  codigo: string;
  /** A ordem da previsão que esta amostra atendeu (só na ficha da OS). */
  ordem?: number | null;
  identificacao_recebida: string | null;
  ponto_investigacao: string | null;
  profundidade_inicial: string | null;
  profundidade_final: string | null;
  tipo_amostra: string | null;
  tipo_acondicionamento: string | null;
  acondicionamento_descricao: string | null;
  data_coleta: string | null;
  status: string;
  latitude?: number | string | null;
  longitude?: number | string | null;
  utm_e?: number | string | null;
  utm_n?: number | string | null;
  utm_fuso?: number | null;
  origem_coordenada?: string | null;
}

/**
 * Uma amostra PREVISTA que ainda não chegou — a linha sem código.
 *
 * Os campos de coordenada são opcionais de propósito: a onda 201 está
 * pondo coordenada na previsão (de projeto ou estimada); quando a
 * `vw_os_amostra_prevista` passar a devolvê-la, a coluna aparece sozinha.
 */
export interface AmostraEsperada {
  id: string;
  ordem: number | null;
  identificacao_prevista: string | null;
  origem_prevista: string | null;
  observacoes: string | null;
  tipo_amostra?: string | null;
  profundidade_inicial?: string | number | null;
  profundidade_final?: string | number | null;
  latitude?: number | string | null;
  longitude?: number | string | null;
  utm_e?: number | string | null;
  utm_n?: number | string | null;
  utm_fuso?: number | null;
  origem_coordenada?: string | null;
  /* 0226 (240, D5): quando deve chegar, e há quanto tempo passou da
     primeira previsão. */
  previsao_chegada?: string | null;
  previsao_origem?: string | null;
  chegada_atrasada_dias?: number | null;
}

type Linha =
  | { tipo: 'recebida'; ordem: number | null; a: AmostraDoQuadro }
  | { tipo: 'esperando'; ordem: number | null; e: AmostraEsperada };

/** 276: o valor de cada coluna para a ordenação na tela (vazio → `null`). */
function chaveDeOrdem(campo: string): (l: Linha) => string | number | null {
  const n = (v: unknown) => (v === null || v === undefined || v === ''
    ? null : Number(v));
  const t = (v: unknown) => (v === null || v === undefined || v === '' ? null : String(v));
  switch (campo) {
    case 'ordem': return (l) => l.ordem;
    case 'codigo': return (l) => (l.tipo === 'recebida' ? l.a.codigo : null);
    case 'identificacao': return (l) => t(l.tipo === 'recebida'
      ? l.a.identificacao_recebida : l.e.identificacao_prevista);
    case 'ponto': return (l) => t(l.tipo === 'recebida'
      ? l.a.ponto_investigacao : l.e.origem_prevista);
    case 'profundidade': return (l) => n(l.tipo === 'recebida'
      ? l.a.profundidade_inicial : l.e.profundidade_inicial);
    case 'tipo': return (l) => t(l.tipo === 'recebida' ? l.a.tipo_amostra : l.e.tipo_amostra);
    case 'coordenada': return (l) => {
      const x = l.tipo === 'recebida' ? l.a : l.e;
      return n(x.utm_e) ?? n(x.latitude);
    };
    case 'situacao': return (l) => (l.tipo === 'recebida' ? rotulo(l.a.status) : 'por chegar');
    default: return () => null;
  }
}

/** O que a edição rápida manda por amostra. Tudo opcional: só o que mudou. */
interface Rascunho {
  identificacaoRecebida?: string;
  pontoInvestigacao?: string;
  profundidadeInicial?: string;
  profundidadeFinal?: string;
  tipoAcondicionamento?: string;
  acondicionamentoDescricao?: string;
  coordA?: string;   // latitude, ou E
  coordB?: string;   // longitude, ou N
}

type Modo = 'utm' | 'graus';

const ORIGENS: Opcao[] = [
  { valor: 'gps', texto: 'GPS' },
  { valor: 'topografia', texto: 'Topografia' },
  { valor: 'projeto', texto: 'Projeto' },
  { valor: 'estimada', texto: 'Estimada' },
];

/*
 * O fuso 22S cobre Blumenau e quase toda a Santa Catarina, que é de onde
 * vem a maior parte do material. Os oito são os do Brasil — o EPSG de
 * cada um sai do fuso no banco (`fn_utm_srid`, 0187).
 */
const FUSOS = [18, 19, 20, 21, 22, 23, 24, 25];

/*
 * ── 268 · a tabela de amostras, uma só (§9.1 da ideia do fluxo) ─────
 *
 * A tabela canônica: [#] · Amostra (código mono + identificação abaixo) ·
 * Furo/Ponto · Profundidade · Tipo · [colunas da tela] · Situação. O que
 * fazia a tabela rolar de lado — acondicionamento e coordenada — foi para
 * a FICHA À DIREITA, que abre ao clicar no código (o padrão aprovado em
 * 28/09, `TabelaComFicha`). Na Edição rápida as colunas voltam, porque é
 * nelas que se digita. A grade da Programação desenha a mesma célula de
 * amostra: código, identificação abaixo, o resto numa linha menor.
 */
export function QuadroDeAmostras({
  amostras, podeEditar, aoSalvo, destacada, titulo, acoes, colunaFotos,
  ordenacao, vazio, nota, esperadas, colunaOrdem, colunaEnsaios,
  aoExcluirEsperada, lixeira, colunaCoordenada, ordenarNaTela, edicaoDasEsperadas,
  selecao, colunaEtiqueta, fichaExtra,
}: {
  amostras: AmostraDoQuadro[];
  podeEditar: boolean;
  aoSalvo(): Promise<void>;
  titulo: ReactNode;
  destacada?: string | null;
  acoes?: ReactNode;
  colunaFotos?: { titulo: string; celula(a: AmostraDoQuadro): ReactNode };
  ordenacao?: {
    ordem: string; sentido: 'asc' | 'desc'; aoTrocar(campo: string): void;
  };
  vazio?: ReactNode;
  /** Uma linha da tela dona, logo acima da tabela (a ordem, na 194). */
  nota?: ReactNode;
  /** As previstas que ainda não chegaram (ficha da OS, 204). */
  esperadas?: AmostraEsperada[];
  /** A coluna «#» com a ordem da previsão (ficha da OS). */
  colunaOrdem?: boolean;
  /** 268: a coluna «Tipo» é canônica — vem sempre. A propriedade fica por compatibilidade. */
  colunaTipo?: boolean;
  /** A coluna «Ensaios», desenhada pela tela dona (ficha da OS). */
  colunaEnsaios?: {
    titulo?: string;
    recebida(a: AmostraDoQuadro): ReactNode;
    esperando(e: AmostraEsperada): ReactNode;
  };
  /** 227: «Excluir» na linha que nunca vai chegar (ficha da OS). */
  aoExcluirEsperada?(e: AmostraEsperada): void;
  /**
   * 276: a lixeira da exclusão definitiva (`RN-214`) no fim da linha da
   * amostra RECEBIDA — só para a conta com a marca, só na homologação, e
   * nunca na Edição rápida. A prevista que não chega já tem o «Excluir»
   * da 227 (`RN-208`), que guarda o motivo; aqui a célula fica vazia.
   */
  lixeira?: boolean;
  /**
   * 276: a coluna «Coordenada» também fora da Edição rápida (ficha da OS):
   * quem confere a locação lê o par sem abrir a edição.
   */
  colunaCoordenada?: boolean;
  /**
   * 276: todas as colunas ordenam no clique, na própria tela (ficha da
   * OS). Sem ordem escolhida vale a da previsão; o clique na mesma coluna
   * inverte, e o terceiro volta à ordem da previsão. O recebimento continua
   * com `ordenacao` (a ordem do lote é gravada no servidor, 194).
   */
  ordenarNaTela?: boolean;
  /**
   * 276 (`RN-228`): o id da OS — com ele, a Edição rápida também corrige as
   * amostras POR CHEGAR (identificação, furo/ponto, profundidade e
   * coordenada), por `PATCH /os/:id/amostras-previstas/edicao-rapida`.
   * Quem passa decide a permissão (`os.programar`).
   */
  edicaoDasEsperadas?: string;
  /**
   * 287: marcar as recebidas — para a janela «Imprimir etiquetas» da OS.
   * Fica fora da Edição rápida (ali a linha inteira é campo).
   */
  selecao?: {
    marcadas: Set<string>;
    aoMarcar(id: string, sim: boolean): void;
    aoMarcarTodas(sim: boolean): void;
  };
  /** 287: a coluna «Etiqueta» — a situação da etiqueta impressa (RN-232). */
  colunaEtiqueta?(a: AmostraDoQuadro): ReactNode;
  /** 287: o que a tela dona acrescenta na ficha da amostra recebida. */
  fichaExtra?(a: AmostraDoQuadro): ReactNode;
}) {
  const comLixeira = useLixeira() && !!lixeira;
  const [ordemLocal, setOrdemLocal] = useState<{ ordem: string; sentido: 'asc' | 'desc' }>(
    { ordem: '', sentido: 'asc' });
  const trocarOrdemLocal = (campo: string) => setOrdemLocal((o) => (o.ordem !== campo
    ? { ordem: campo, sentido: 'asc' }
    : o.sentido === 'asc' ? { ordem: campo, sentido: 'desc' } : { ordem: '', sentido: 'asc' }));
  const [editando, setEditando] = useState(false);
  const ord = ordenacao ?? (ordenarNaTela && !editando
    ? { ...ordemLocal, aoTrocar: trocarOrdemLocal } : undefined);
  const mostraCoordenada = editando || !!colunaCoordenada;
  const [rascunhos, setRascunhos] = useState<Record<string, Rascunho>>({});
  const [modo, setModo] = useState<Modo>('utm');
  const [fuso, setFuso] = useState(22);
  const [origem, setOrigem] = useState('gps');
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<unknown>(null);
  const [pedindoMotivo, setPedindoMotivo] = useState(false);
  const [motivo, setMotivo] = useState('');
  /* 268: a linha aberta na ficha à direita (`esp-<id>` para a prevista). */
  const [aberta, setAberta] = useState<string | null>(null);

  /* Recarregou por fora (outra onda, outra aba): o rascunho não sobrevive. */
  useEffect(() => { if (!editando) setRascunhos({}); }, [editando]);

  const mexer = (id: string, campo: keyof Rascunho, valor: string) =>
    setRascunhos((r) => ({ ...r, [id]: { ...r[id], [campo]: valor } }));

  const valor = (a: AmostraDoQuadro, campo: keyof Rascunho, original: string) =>
    rascunhos[a.id]?.[campo] ?? original;

  /** O que de fato mudou, no formato que a API espera. */
  const itens = useMemo(() => {
    const saida: Array<Record<string, unknown>> = [];
    for (const a of amostras) {
      const r = rascunhos[a.id];
      if (!r) continue;
      const item: Record<string, unknown> = { id: a.id };
      const texto = (novo: string | undefined, velho: string | null) => {
        if (novo === undefined) return undefined;
        const limpo = novo.trim();
        return limpo === (velho ?? '') ? undefined : limpo;
      };
      const num = (novo: string | undefined, velho: unknown) => {
        if (novo === undefined) return undefined;
        const n = paraNumero(novo);
        if (n === undefined) return undefined;
        return Number(velho) === n ? undefined : n;
      };

      const ident = texto(r.identificacaoRecebida, a.identificacao_recebida);
      if (ident !== undefined) item.identificacaoRecebida = ident;
      const ponto = texto(r.pontoInvestigacao, a.ponto_investigacao);
      if (ponto !== undefined) item.pontoInvestigacao = ponto;
      const acond = texto(r.tipoAcondicionamento, a.tipo_acondicionamento);
      if (acond) item.tipoAcondicionamento = acond;
      const descr = texto(r.acondicionamentoDescricao, a.acondicionamento_descricao);
      if (descr !== undefined) item.acondicionamentoDescricao = descr;

      const pi = num(r.profundidadeInicial, a.profundidade_inicial);
      if (pi !== undefined) item.profundidadeInicial = pi;
      const pf = num(r.profundidadeFinal, a.profundidade_final);
      if (pf !== undefined) item.profundidadeFinal = pf;

      /*
       * A coordenada só vai quando o PAR está completo: meia coordenada
       * é um alfinete no meio do oceano. O DTO também recusa, mas
       * recusar aqui poupa a viagem.
       */
      const a1 = paraNumero(r.coordA);
      const b1 = paraNumero(r.coordB);
      if (a1 !== undefined && b1 !== undefined) {
        if (modo === 'utm') {
          item.utmE = a1; item.utmN = b1; item.utmFuso = fuso;
        } else {
          item.latitude = a1; item.longitude = b1;
        }
        item.origemCoordenada = origem;
      }

      if (Object.keys(item).length > 1) saida.push(item);
    }
    return saida;
  }, [amostras, rascunhos, modo, fuso, origem]);

  /*
   * 276: o mesmo para as previstas. O rascunho delas mora em `esp-<id>`.
   * A origem da coordenada de uma previsão só pode ser «projeto» ou
   * «estimada» (0190): GPS e topografia viram «projeto».
   */
  const itensEsperadas = useMemo(() => {
    const saida: Array<Record<string, unknown>> = [];
    if (!edicaoDasEsperadas) return saida;
    for (const e of esperadas ?? []) {
      const r = rascunhos[`esp-${e.id}`];
      if (!r) continue;
      const item: Record<string, unknown> = { id: e.id };
      const texto = (novo: string | undefined, velho: string | null | undefined) => {
        if (novo === undefined) return undefined;
        const limpo = novo.trim();
        return limpo === (velho ?? '') ? undefined : limpo;
      };
      const ident = texto(r.identificacaoRecebida, e.identificacao_prevista);
      if (ident !== undefined) item.identificacaoPrevista = ident;
      const ponto = texto(r.pontoInvestigacao, e.origem_prevista);
      if (ponto !== undefined) item.origemPrevista = ponto;
      const prof = (novo: string | undefined, velho: unknown) => {
        if (novo === undefined) return undefined;
        if (novo.trim() === '') return velho === null || velho === undefined ? undefined : null;
        const n = paraNumero(novo);
        if (n === undefined) return undefined;
        return Number(velho) === n && velho !== null && velho !== undefined ? undefined : n;
      };
      const pi = prof(r.profundidadeInicial, e.profundidade_inicial);
      if (pi !== undefined) item.profundidadeInicial = pi;
      const pf = prof(r.profundidadeFinal, e.profundidade_final);
      if (pf !== undefined) item.profundidadeFinal = pf;
      const a1 = paraNumero(r.coordA);
      const b1 = paraNumero(r.coordB);
      if (a1 !== undefined && b1 !== undefined) {
        if (modo === 'utm') {
          item.utmE = a1; item.utmN = b1; item.utmFuso = fuso;
        } else {
          item.latitude = a1; item.longitude = b1;
        }
        item.origemCoordenada = origem === 'estimada' ? 'estimada' : 'projeto';
      }
      if (Object.keys(item).length > 1) saida.push(item);
    }
    return saida;
  }, [edicaoDasEsperadas, esperadas, rascunhos, modo, fuso, origem]);

  async function salvar(comMotivo?: string) {
    if (!itens.length && !itensEsperadas.length) {
      setEditando(false); setRascunhos({}); return;
    }
    setErro(null); setSalvando(true);
    try {
      /* 276: as previstas primeiro — não pedem motivo; reenviar o mesmo
         valor depois da janela do motivo não muda nada. */
      if (itensEsperadas.length && edicaoDasEsperadas) {
        await api(`/os/${edicaoDasEsperadas}/amostras-previstas/edicao-rapida`, {
          metodo: 'PATCH', corpo: { itens: itensEsperadas },
        });
      }
      if (itens.length) {
        await api('/amostras/edicao-rapida', {
          metodo: 'PATCH',
          corpo: comMotivo ? { itens, motivo: comMotivo } : { itens },
        });
      }
      setPedindoMotivo(false); setMotivo('');
      setEditando(false); setRascunhos({});
      await aoSalvo();
    } catch (e) {
      /*
       * A API recusa a correção de um campo JÁ PREENCHIDO sem motivo
       * (UX-042; a regra virou «só quando é alteração» em 21/09/2026).
       * Preencher o que estava em branco passa direto — e é por isso que
       * a janela não aparece no caso comum.
       */
      if (e instanceof ErroApi && e.campos.some((c) => c.campo === 'motivo')) {
        setPedindoMotivo(true);
      } else {
        setErro(e);
      }
    } finally { setSalvando(false); }
  }

  const totalEditado = itens.length + itensEsperadas.length;
  const barra = ((podeEditar && amostras.length > 0)
    || (!!edicaoDasEsperadas && (esperadas ?? []).length > 0)) && (
    <button
      type="button"
      className={editando ? 'botao' : 'botao discreto'}
      disabled={salvando}
      onClick={() => (editando ? salvar() : setEditando(true))}
    >
      {salvando ? 'Gravando…'
        : editando
          ? (totalEditado
            ? `Salvar ${totalEditado} amostra(s)` : 'Salvar edição')
          : 'Edição rápida'}
    </button>
  );

  /*
   * As linhas, intercaladas pela ordem da previsão. Sem `colunaOrdem` a
   * tabela é a de sempre (o recebimento manda a ordem do lote, 194);
   * com ela, a recebida entra no lugar da previsão que atendeu, e o que
   * chegou sem previsão vai para o fim, na ordem em que veio.
   */
  const linhas = useMemo<Linha[]>(() => {
    const todas: Linha[] = [
      ...amostras.map((a) => ({ tipo: 'recebida' as const, ordem: a.ordem ?? null, a })),
      ...(esperadas ?? []).map((e) => ({ tipo: 'esperando' as const, ordem: e.ordem, e })),
    ];
    const naPrevisao = !colunaOrdem ? todas : todas
      .map((l, i) => ({ l, i }))
      .sort((x, y) => {
        const a = x.l.ordem, b = y.l.ordem;
        if (a === null && b === null) return x.i - y.i;
        if (a === null) return 1;
        if (b === null) return -1;
        return a - b || x.i - y.i;
      })
      .map((x) => x.l);
    if (!ordenarNaTela || ordenacao || !ordemLocal.ordem) return naPrevisao;
    const chave = chaveDeOrdem(ordemLocal.ordem);
    const fator = ordemLocal.sentido === 'asc' ? 1 : -1;
    return naPrevisao
      .map((l, i) => ({ l, i, k: chave(l) }))
      .sort((x, y) => {
        /* vazio vai sempre para o fim, nos dois sentidos */
        if (x.k === null && y.k === null) return x.i - y.i;
        if (x.k === null) return 1;
        if (y.k === null) return -1;
        const c = typeof x.k === 'number' && typeof y.k === 'number'
          ? x.k - y.k
          : String(x.k).localeCompare(String(y.k), 'pt-BR', { numeric: true, sensitivity: 'base' });
        return c * fator || x.i - y.i;
      })
      .map((x) => x.l);
  }, [amostras, esperadas, colunaOrdem, ordenarNaTela, ordenacao, ordemLocal]);

  if (!linhas.length) return <>{vazio ?? <Vazio>Nenhuma amostra.</Vazio>}</>;

  return (
    <>
      <div className="linha-topo">
        <h2 className="cresce" style={{ marginBottom: '.6rem' }}>{titulo}</h2>
        {editando && (
          <button
            type="button" className="botao secundario" disabled={salvando}
            onClick={() => { setEditando(false); setRascunhos({}); setErro(null); }}
          >
            Cancelar
          </button>
        )}
        {barra}
        {acoes}
      </div>

      <ErroDaApi erro={erro} />

      {editando && (
        /*
         * A barra das coordenadas vale para a TABELA inteira, e não por
         * linha: quem está digitando a planilha de locação está no mesmo
         * sistema e no mesmo fuso das trinta amostras. Um seletor por
         * linha seria trinta vezes a mesma escolha — e trinta chances de
         * errar uma.
         */
        <div className="cartao faixa-edicao">
          <div className="linha">
            <label className="campo campo-embutido">
              <span className="rotulo">Coordenadas</span>
              <select
                value={modo}
                onChange={(e) => setModo(e.currentTarget.value as Modo)}
              >
                <option value="utm">UTM (SIRGAS 2000)</option>
                <option value="graus">Geográficas (graus decimais)</option>
              </select>
            </label>
            {modo === 'utm' && (
              <label className="campo campo-embutido">
                <span className="rotulo">Fuso</span>
                <select
                  value={fuso}
                  onChange={(e) => setFuso(Number(e.currentTarget.value))}
                >
                  {FUSOS.map((f) => (
                    <option key={f} value={f}>{f}S</option>
                  ))}
                </select>
              </label>
            )}
            <label className="campo campo-embutido">
              <span className="rotulo">Origem da coordenada</span>
              <select
                value={origem}
                onChange={(e) => setOrigem(e.currentTarget.value)}
              >
                {ORIGENS.map((o) => (
                  <option key={o.valor} value={o.valor}>{o.texto}</option>
                ))}
              </select>
            </label>
          </div>
          <p className="sub" style={{ marginBottom: 0 }}>
            A coordenada da amostra é o que o <b>Mapa Geotécnico</b> usa para
            pôr o alfinete no ponto de coleta; sem ela, o alfinete cai na obra.
            Digite o par completo — meia coordenada não é gravada.
            {edicaoDasEsperadas && (esperadas ?? []).length > 0 && (
              <> Nas amostras <b>por chegar</b> a origem fica «Projeto» (ou
              «Estimada», se escolhida): é onde se pretende coletar.</>
            )}
          </p>
        </div>
      )}

      <div className="cartao">
        {nota}
        <div className={`tabela-amostras${aberta && !editando ? ' com-ficha' : ''}`}>
        <div className="rolagem">
          <table className="dados">
            <thead>
              <tr>
                {selecao && !editando && (
                  <th scope="col" className="col-marcar">
                    <input type="checkbox" aria-label="Marcar todas as recebidas"
                      checked={amostras.length > 0 && amostras.every((a) => selecao.marcadas.has(a.id))}
                      onChange={(ev) => selecao.aoMarcarTodas(ev.currentTarget.checked)} />
                  </th>
                )}
                {colunaOrdem && (ord && ordenarNaTela && !ordenacao && !editando ? (
                  <ColunaOrdenavel campo="ordem" texto="#" ordem={ord.ordem}
                    sentido={ord.sentido} aoTrocar={ord.aoTrocar} />
                ) : <th>#</th>)}
                {ord ? (
                  <th scope="col" className={['ordenavel col-amostra',
                    ['codigo', 'identificacao'].includes(ord.ordem) ? 'ativa' : '']
                    .filter(Boolean).join(' ')}
                    aria-sort={['codigo', 'identificacao'].includes(ord.ordem)
                      ? (ord.sentido === 'asc' ? 'ascending' : 'descending') : 'none'}>
                    <BotaoDeOrdem campo="codigo" texto="Amostra" o={ord} />
                    {/* 268: a identificação mora sob o código, e ordena dali. */}
                    {!editando && (
                      <BotaoDeOrdem campo="identificacao" texto="identificação" o={ord} menor />
                    )}
                  </th>
                ) : <th className="col-amostra">Amostra</th>}
                {colunaFotos && <th>{colunaFotos.titulo}</th>}
                {editando && <th>Identificação</th>}
                {ord ? (
                  <ColunaOrdenavel
                    campo="ponto" texto="Furo/Ponto"
                    ordem={ord.ordem} sentido={ord.sentido}
                    aoTrocar={ord.aoTrocar}
                  />
                ) : <th>Furo/Ponto</th>}
                {(() => {
                  /* 276: o resto ordena só na tela da OS, e nunca na edição. */
                  const tela = ord && ordenarNaTela && !ordenacao && !editando ? ord : null;
                  const th = (campo: string, texto: string) => (tela
                    ? <ColunaOrdenavel key={campo} campo={campo} texto={texto}
                      ordem={tela.ordem} sentido={tela.sentido} aoTrocar={tela.aoTrocar} />
                    : <th key={campo}>{texto}</th>);
                  return (<>
                    {th('profundidade', 'Profundidade (m)')}
                    {th('tipo', 'Tipo')}
                    {editando && <th>Acondicionamento</th>}
                    {mostraCoordenada && th('coordenada', 'Coordenada')}
                    {colunaEnsaios && <th>{colunaEnsaios.titulo ?? 'Ensaios'}</th>}
                    {colunaEtiqueta && !editando && <th>Etiqueta</th>}
                    {th('situacao', 'Situação')}
                  </>);
                })()}
                {comLixeira && !editando && (
                  <th scope="col" className="col-lixeira" aria-label="Excluir" />
                )}
              </tr>
            </thead>
            <tbody>
              {linhas.map((l) => (l.tipo === 'esperando' ? (
                <LinhaEsperando
                  key={`esp-${l.e.id}`} e={l.e} modo={modo} editando={editando}
                  colunaOrdem={colunaOrdem} colunaFotos={Boolean(colunaFotos)}
                  colunaEnsaios={colunaEnsaios}
                  colunaMarcar={!!selecao && !editando}
                  colunaEtiqueta={!!colunaEtiqueta && !editando}
                  aoExcluir={aoExcluirEsperada}
                  colunaLixeira={comLixeira && !editando}
                  colunaCoordenada={mostraCoordenada}
                  editavel={editando && !!edicaoDasEsperadas}
                  valor={(campo, original) => rascunhos[`esp-${l.e.id}`]?.[campo] ?? original}
                  mexer={(campo, v) => mexer(`esp-${l.e.id}`, campo, v)}
                  aberta={aberta === `esp-${l.e.id}`}
                  aoAbrir={() => setAberta(aberta === `esp-${l.e.id}` ? null : `esp-${l.e.id}`)}
                />
              ) : (
                <tr
                  key={l.a.id}
                  className={aberta === l.a.id && !editando ? 'na-ficha' : undefined}
                  style={destacada === l.a.id
                    ? { outline: '2px solid var(--oliva)' } : undefined}
                >
                  {selecao && !editando && (
                    <td className="col-marcar">
                      <input type="checkbox" aria-label={`Marcar ${nomeDaAmostra(daApi(l.a))}`}
                        checked={selecao.marcadas.has(l.a.id)}
                        onChange={(ev) => selecao.aoMarcar(l.a.id, ev.currentTarget.checked)} />
                    </td>
                  )}
                  {colunaOrdem && (
                    <td className="mono sub">{l.a.ordem ?? ''}</td>
                  )}
                  <td className="col-amostra">
                    {/* Editando, a identificação é um campo ao lado: aqui fica
                        o código, a referência que não muda enquanto se digita. */}
                    {editando ? <b className="mono">{l.a.codigo}</b> : (
                      /* RN-248 (onda 303): o nome do cliente em destaque e o
                         código AM embaixo — a inversão da célula da 268. */
                      <button type="button" className="abrir-amostra"
                        aria-expanded={aberta === l.a.id}
                        title="Abrir a ficha da amostra — acondicionamento, coordenada e situação"
                        onClick={() => setAberta(aberta === l.a.id ? null : l.a.id)}>
                        <NomeDaAmostra amostra={daApi(l.a)} />
                      </button>
                    )}
                  </td>
                  {colunaFotos && <td className="num">{colunaFotos.celula(l.a)}</td>}

                  {editando && (
                    <td>
                      <input
                        className="cel" type="text" maxLength={200}
                        aria-label={`Identificação de ${l.a.codigo}`}
                        value={valor(l.a, 'identificacaoRecebida', l.a.identificacao_recebida ?? '')}
                        onChange={(e) => mexer(l.a.id, 'identificacaoRecebida', e.currentTarget.value)}
                      />
                    </td>
                  )}

                  <td>
                    {editando ? (
                      <input
                        className="cel" type="text" maxLength={120}
                        aria-label={`Furo ou ponto de ${l.a.codigo}`}
                        value={valor(l.a, 'pontoInvestigacao', l.a.ponto_investigacao ?? '')}
                        onChange={(e) => mexer(l.a.id, 'pontoInvestigacao', e.currentTarget.value)}
                      />
                    ) : (l.a.ponto_investigacao ?? <span className="nada">—</span>)}
                  </td>

                  <td className="num">
                    {editando ? (
                      <span className="cel-par">
                        <input
                          className="cel num" inputMode="decimal"
                          aria-label={`Profundidade inicial de ${l.a.codigo}`}
                          placeholder="de"
                          value={valor(l.a, 'profundidadeInicial', paraCampo(l.a.profundidade_inicial, 3))}
                          onChange={(e) => mexer(l.a.id, 'profundidadeInicial', e.currentTarget.value)}
                        />
                        <input
                          className="cel num" inputMode="decimal"
                          aria-label={`Profundidade final de ${l.a.codigo}`}
                          placeholder="até"
                          value={valor(l.a, 'profundidadeFinal', paraCampo(l.a.profundidade_final, 3))}
                          onChange={(e) => mexer(l.a.id, 'profundidadeFinal', e.currentTarget.value)}
                        />
                      </span>
                    ) : faixaProfundidade(l.a.profundidade_inicial, l.a.profundidade_final)}
                  </td>

                  <td>{l.a.tipo_amostra ? rotulo(l.a.tipo_amostra) : <span className="nada">—</span>}</td>

                  {editando && (
                    <td>
                      <CelulaAcondicionamento
                        a={l.a}
                        valorTipo={valor(l.a, 'tipoAcondicionamento', l.a.tipo_acondicionamento ?? '')}
                        valorDescricao={valor(l.a, 'acondicionamentoDescricao', l.a.acondicionamento_descricao ?? '')}
                        aoMexer={(campo, v) => mexer(l.a.id, campo, v)}
                      />
                    </td>
                  )}

                  {editando && (
                    <td className="num">
                      <span className="cel-par">
                        <input
                          className="cel num" inputMode="decimal"
                          aria-label={modo === 'utm'
                            ? `Coordenada E de ${l.a.codigo}` : `Latitude de ${l.a.codigo}`}
                          placeholder={modo === 'utm' ? 'E' : 'lat'}
                          value={valor(l.a, 'coordA', paraCampo(
                            modo === 'utm' ? l.a.utm_e : l.a.latitude, modo === 'utm' ? 2 : 7))}
                          onChange={(e) => mexer(l.a.id, 'coordA', e.currentTarget.value)}
                        />
                        <input
                          className="cel num" inputMode="decimal"
                          aria-label={modo === 'utm'
                            ? `Coordenada N de ${l.a.codigo}` : `Longitude de ${l.a.codigo}`}
                          placeholder={modo === 'utm' ? 'N' : 'long'}
                          value={valor(l.a, 'coordB', paraCampo(
                            modo === 'utm' ? l.a.utm_n : l.a.longitude, modo === 'utm' ? 2 : 7))}
                          onChange={(e) => mexer(l.a.id, 'coordB', e.currentTarget.value)}
                        />
                      </span>
                    </td>
                  )}

                  {!editando && colunaCoordenada && (
                    <td className="num"><Coordenada a={l.a} modo={modo} /></td>
                  )}

                  {colunaEnsaios && <td>{colunaEnsaios.recebida(l.a)}</td>}
                  {colunaEtiqueta && !editando && <td>{colunaEtiqueta(l.a)}</td>}
                  <td>
                    {/* 256 (§9.2): a situação da recebida com selo, nunca só cor. */}
                    <Selo tom={TOM_DA_AMOSTRA[l.a.status] ?? 'neutro'}>
                      {rotulo(l.a.status)}
                    </Selo>
                  </td>
                  {comLixeira && !editando && (
                    <td className="col-lixeira">
                      <Lixeira tipo="amostra" id={l.a.id} rotulo={nomeDaAmostra(daApi(l.a))}
                        aoApagar={() => { setAberta(null); void aoSalvo(); }} />
                    </td>
                  )}
                </tr>
              )))}
            </tbody>
          </table>
        </div>
        {aberta && !editando && (() => {
          const l = linhas.find((x) => (x.tipo === 'recebida' ? x.a.id : `esp-${x.e.id}`) === aberta);
          if (!l) return null;
          return (
            <FichaDaAmostra l={l} modo={modo} aoFechar={() => setAberta(null)}
              extra={l.tipo === 'recebida' && fichaExtra ? fichaExtra(l.a) : null} />
          );
        })()}
        </div>
      </div>

      <Dialogo
        aberto={pedindoMotivo}
        titulo="Corrigir a ficha da amostra"
        aoFechar={() => setPedindoMotivo(false)}
      >
        <p className="sub">
          Uma das correções <b>sobrescreve</b> um campo que já tinha valor, e o
          laudo cita esse campo. A trilha guarda o valor antigo, o novo e o
          motivo — preencher o que estava em branco não pede nada disso.
        </p>
        <label className="campo">
          <span className="rotulo">Motivo da correção</span>
          <textarea
            rows={3} value={motivo} autoFocus
            onChange={(e) => setMotivo(e.currentTarget.value)}
            placeholder="A etiqueta do saco dizia SP-02; o boletim de campo diz SP-03."
          />
        </label>
        <div className="linha">
          <button
            type="button" className="botao"
            disabled={motivo.trim().length < 5 || salvando}
            onClick={() => salvar(motivo.trim())}
          >
            {salvando ? 'Gravando…' : 'Gravar a correção'}
          </button>
          <button
            type="button" className="botao secundario"
            onClick={() => setPedindoMotivo(false)}
          >
            Cancelar
          </button>
        </div>
      </Dialogo>
    </>
  );
}

/**
 * A linha de uma amostra que ainda não chegou.
 *
 * Mesmas colunas da recebida, para o olho não pular: o que a previsão
 * sabe (identificação, furo, profundidade, tipo, ensaios marcados) vem
 * preenchido; o que só existe depois de o material chegar (código,
 * acondicionamento, situação) vem como «Esperando». Nada aqui se edita.
 */
function LinhaEsperando({
  e, modo, editando, colunaOrdem, colunaFotos, colunaEnsaios, aoExcluir, aberta, aoAbrir,
  colunaLixeira, colunaCoordenada, editavel, valor, mexer, colunaMarcar, colunaEtiqueta,
}: {
  /** 287: a célula vazia da marcação e a «Etiqueta» de quem ainda não chegou. */
  colunaMarcar?: boolean;
  colunaEtiqueta?: boolean;
  aoExcluir?(e: AmostraEsperada): void;
  colunaLixeira?: boolean;
  colunaCoordenada?: boolean;
  /** 276: na Edição rápida, a prevista também se corrige. */
  editavel?: boolean;
  valor?(campo: keyof Rascunho, original: string): string;
  mexer?(campo: keyof Rascunho, v: string): void;
  e: AmostraEsperada;
  modo: Modo;
  editando: boolean;
  colunaOrdem?: boolean;
  colunaFotos: boolean;
  colunaEnsaios?: { esperando(e: AmostraEsperada): ReactNode };
  aberta: boolean;
  aoAbrir(): void;
}) {
  const nome = e.identificacao_prevista ?? 'amostra por chegar';
  const v = (campo: keyof Rascunho, original: string) => valor?.(campo, original) ?? original;
  const m = (campo: keyof Rascunho) => (ev: { currentTarget: { value: string } }) =>
    mexer?.(campo, ev.currentTarget.value);
  const cel = (campo: keyof Rascunho, original: string, rotuloCampo: string,
    extra: { num?: boolean; placeholder?: string; max?: number } = {}) => (
    <input
      className={extra.num ? 'cel num' : 'cel'} type="text"
      inputMode={extra.num ? 'decimal' : undefined} maxLength={extra.max}
      aria-label={`${rotuloCampo} de ${nome}`} placeholder={extra.placeholder}
      value={v(campo, original)} onChange={m(campo)}
    />
  );
  return (
    <tr className={`esperando${aberta && !editando ? ' na-ficha' : ''}`}>
      {colunaMarcar && <td className="col-marcar" />}
      {colunaOrdem && <td className="mono sub">{e.ordem ?? ''}</td>}
      {/* 256 (§9.2): um nome só para «ainda não chegou». */}
      <td className="col-amostra">
        {editando ? <Selo tom="neutro">○ Por chegar</Selo> : (
          <button type="button" className="abrir-amostra" aria-expanded={aberta}
            title="Abrir a ficha da amostra prevista" onClick={aoAbrir}>
            <Selo tom="neutro">○ Por chegar</Selo>
          </button>
        )}
        {!editando && (
          <span className="ident-amostra">
            {e.identificacao_prevista ?? <span className="nada">sem identificação</span>}
          </span>
        )}
        {!editando && e.observacoes && <span className="sub obs-prevista">{e.observacoes}</span>}
      </td>
      {colunaFotos && <td className="num"><span className="nada">—</span></td>}
      {editando && (editavel ? (
        <td>{cel('identificacaoRecebida', e.identificacao_prevista ?? '', 'Identificação', { max: 120 })}</td>
      ) : (
        <td>
          {e.identificacao_prevista
            ? <span className="sub">{e.identificacao_prevista}</span>
            : <span className="nada">sem identificação</span>}
          {e.observacoes && <div className="sub">{e.observacoes}</div>}
        </td>
      ))}
      <td>
        {editavel
          ? cel('pontoInvestigacao', e.origem_prevista ?? '', 'Furo ou ponto', { max: 120 })
          : (e.origem_prevista ?? <span className="nada">—</span>)}
      </td>
      <td className="num">
        {editavel ? (
          <span className="cel-par">
            {cel('profundidadeInicial', paraCampo(e.profundidade_inicial ?? null, 3),
              'Profundidade inicial', { num: true, placeholder: 'de' })}
            {cel('profundidadeFinal', paraCampo(e.profundidade_final ?? null, 3),
              'Profundidade final', { num: true, placeholder: 'até' })}
          </span>
        ) : (faixaProfundidade(e.profundidade_inicial, e.profundidade_final)
          || <span className="nada">—</span>)}
      </td>
      <td>{e.tipo_amostra ? rotulo(e.tipo_amostra) : <span className="nada">—</span>}</td>
      {editando && <td><span className="nada">—</span></td>}
      {colunaCoordenada && (editavel ? (
        <td className="num">
          <span className="cel-par">
            {cel('coordA', paraCampo(
              (modo === 'utm' ? e.utm_e : e.latitude) ?? null, modo === 'utm' ? 2 : 7),
            modo === 'utm' ? 'Coordenada E' : 'Latitude',
            { num: true, placeholder: modo === 'utm' ? 'E' : 'lat' })}
            {cel('coordB', paraCampo(
              (modo === 'utm' ? e.utm_n : e.longitude) ?? null, modo === 'utm' ? 2 : 7),
            modo === 'utm' ? 'Coordenada N' : 'Longitude',
            { num: true, placeholder: modo === 'utm' ? 'N' : 'long' })}
          </span>
        </td>
      ) : <td className="num"><Coordenada a={e} modo={modo} /></td>)}
      {colunaEnsaios && <td>{colunaEnsaios.esperando(e)}</td>}
      {colunaEtiqueta && <td><span className="nada">— ainda não chegou</span></td>}
      <td>
        <span className="nada">por chegar</span>
        {e.previsao_chegada && (
          <span className="previsao-chegada">
            previsto {data(e.previsao_chegada)}
            {e.previsao_origem === 'testesolo' ? ' · coleta Testesolo' : ''}
          </span>
        )}
        {Number(e.chegada_atrasada_dias) > 0 && (
          <Selo tom="atencao" titulo="Passou da primeira previsão de chegada">
            Atrasada {e.chegada_atrasada_dias} d
          </Selo>
        )}
        {aoExcluir && (
          <button type="button" className="ligacao perigo excluir-prevista"
            title="Esta amostra não vai chegar — excluir da OS"
            onClick={() => aoExcluir(e)}>
            Excluir
          </button>
        )}
      </td>
      {colunaLixeira && <td className="col-lixeira" />}
    </tr>
  );
}

/** O botão de ordenar dentro do cabeçalho «Amostra» (268). */
function BotaoDeOrdem({ campo, texto, o, menor }: {
  campo: string; texto: string; menor?: boolean;
  o: { ordem: string; sentido: 'asc' | 'desc'; aoTrocar(campo: string): void };
}) {
  const ativa = o.ordem === campo;
  return (
    <button type="button" className={menor ? 'ordem-menor' : undefined}
      onClick={() => o.aoTrocar(campo)}
      title={ativa
        ? `Ordenado por ${texto}, ${o.sentido === 'asc' ? 'crescente' : 'decrescente'} — clique para inverter`
        : `Ordenar por ${texto}`}>
      {texto}
      <span className="seta" aria-hidden="true">
        {ativa ? (o.sentido === 'asc' ? '▲' : '▼') : '↕'}
      </span>
    </button>
  );
}

/**
 * A ficha à direita (268, `TabelaComFicha` do 28/09): o que saiu da tabela
 * para ela caber sem rolar de lado — acondicionamento, coordenada, a data
 * da coleta — e o caminho para a ficha completa da amostra.
 */
function FichaDaAmostra({ l, modo, aoFechar, extra }: {
  l: Linha; modo: Modo; aoFechar(): void;
  /** 287: a seção da tela dona (a «Etiqueta», na OS). */
  extra?: ReactNode;
}) {
  const dl = (rot: string, v: ReactNode) => (
    <div className="campo-ficha"><dt>{rot}</dt><dd>{v ?? <span className="nada">—</span>}</dd></div>
  );
  if (l.tipo === 'esperando') {
    const e = l.e;
    return (
      <aside className="ficha-amostra-tab" aria-label="Ficha da amostra">
        <header>
          <div>
            <p className="rot">Amostra prevista</p>
            <h3>{e.identificacao_prevista ?? 'Por chegar'}</h3>
            <p className="sub">○ Por chegar{e.previsao_chegada ? ` · previsto ${data(e.previsao_chegada)}` : ''}</p>
          </div>
          <button type="button" className="fechar-ficha" aria-label="Fechar a ficha" onClick={aoFechar}>×</button>
        </header>
        <dl>
          {dl('Furo/Ponto', e.origem_prevista)}
          {dl('Profundidade (m)', faixaProfundidade(e.profundidade_inicial, e.profundidade_final) || null)}
          {dl('Tipo', e.tipo_amostra ? rotulo(e.tipo_amostra) : null)}
          {dl('Coordenada', <Coordenada a={e} modo={modo} />)}
          {dl('Observações', e.observacoes)}
        </dl>
      </aside>
    );
  }
  const a = l.a;
  return (
    <aside className="ficha-amostra-tab" aria-label="Ficha da amostra">
      <header>
        <div>
          <p className="rot">Amostra</p>
          <h3><NomeDaAmostra amostra={daApi(a)} forte={false} /></h3>
        </div>
        <button type="button" className="fechar-ficha" aria-label="Fechar a ficha" onClick={aoFechar}>×</button>
      </header>
      <dl>
        {dl('Situação', <Selo tom={TOM_DA_AMOSTRA[a.status] ?? 'neutro'}>{rotulo(a.status)}</Selo>)}
        {dl('Furo/Ponto', a.ponto_investigacao)}
        {dl('Profundidade (m)', faixaProfundidade(a.profundidade_inicial, a.profundidade_final) || null)}
        {dl('Tipo', a.tipo_amostra ? rotulo(a.tipo_amostra) : null)}
        {dl(rotuloDoAcondicionamento(a.tipo_amostra), a.tipo_acondicionamento
          ? `${rotulo(a.tipo_acondicionamento)}${a.acondicionamento_descricao ? ` — ${a.acondicionamento_descricao}` : ''}`
          : null)}
        {dl('Coordenada', <Coordenada a={a} modo={modo} />)}
        {dl('Coleta', a.data_coleta ? data(a.data_coleta) : null)}
      </dl>
      {extra}
      <div className="acoes-ficha">
        <Link className="botao" to={`/amostra/${encodeURIComponent(a.codigo)}`}>Abrir a amostra</Link>
      </div>
    </aside>
  );
}

/**
 * O acondicionamento depende do tipo da amostra — e o rótulo também.
 *
 * «O termo que podemos utilizar para acondicionamento é método de
 *  coleta, pois Bloco, Shelby, Denison, Anel ou outro são métodos de
 *  coleta» (Testesolo, 23/08/2026). A lista vem do tipo; a edição rápida
 *  NÃO troca o tipo, porque trocar deformada por indeformada não é
 *  corrigir uma digitação — é dizer que chegou outra coisa.
 */
function CelulaAcondicionamento({
  a, valorTipo, valorDescricao, aoMexer,
}: {
  a: AmostraDoQuadro;
  valorTipo: string;
  valorDescricao: string;
  aoMexer(campo: keyof Rascunho, v: string): void;
}) {
  const lista = ACONDICIONAMENTO[a.tipo_amostra ?? 'deformada'] ?? [];
  return (
    <span className="cel-par">
      <select
        className="cel"
        aria-label={`${rotuloDoAcondicionamento(a.tipo_amostra)} de ${nomeDaAmostra(daApi(a))}`}
        value={valorTipo}
        onChange={(e) => aoMexer('tipoAcondicionamento', e.currentTarget.value)}
      >
        <option value="">—</option>
        {lista.map((v) => (
          <option key={v} value={v}>{rotulo(v)}</option>
        ))}
      </select>
      {valorTipo === 'outro' && (
        <input
          className="cel" type="text" maxLength={200} placeholder="o que é"
          aria-label={`Descrição do acondicionamento de ${nomeDaAmostra(daApi(a))}`}
          value={valorDescricao}
          onChange={(e) => aoMexer('acondicionamentoDescricao', e.currentTarget.value)}
        />
      )}
    </span>
  );
}

/** A coordenada como ela é lida — no sistema que a barra escolheu. */
function Coordenada({ a, modo }: {
  a: Pick<AmostraDoQuadro, 'latitude' | 'longitude' | 'utm_e' | 'utm_n' | 'utm_fuso' | 'origem_coordenada'>;
  modo: Modo;
}) {
  const temUtm = a.utm_e !== null && a.utm_e !== undefined;
  const temGraus = a.latitude !== null && a.latitude !== undefined;
  if (!temUtm && !temGraus) return <span className="nada">—</span>;

  const texto = modo === 'utm' && temUtm
    ? `${numero(a.utm_e, 0)} E · ${numero(a.utm_n, 0)} N · ${a.utm_fuso}S`
    : `${numero(a.latitude, 6)} · ${numero(a.longitude, 6)}`;

  return (
    <span className="mono" title={a.origem_coordenada
      ? `Coordenada de ${rotulo(a.origem_coordenada).toLowerCase()}`
      : undefined}
    >
      {texto}
      {a.origem_coordenada && (
        <span className="selo neutro" style={{ marginLeft: '.35rem' }}>
          {rotulo(a.origem_coordenada)}
        </span>
      )}
    </span>
  );
}

/** O nome canônico (268): a tabela de amostras da OS, do recebimento e da programação. */
export const TabelaDeAmostras = QuadroDeAmostras;
